import React, { Dispatch, useContext } from 'react';
import { IEvent } from 'model/v2';
import { SIDEBAR_ACTIONS } from '../AppointmentSidebar';
import { HeaderViewWrapper } from './style';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faPen, faEye } from '@fortawesome/free-solid-svg-icons';
import { IModalDownStreamsConfigurations } from 'utils/downstreams/DownStreamModalConfigurations';
import { CloseOutlined } from '@ant-design/icons';
import { formRedirectHandler } from 'helpers/calendarHelper';
import { EVENT_STATUS } from 'utils/constants/status';
import { plannerContext } from '..';
import { FEATURES, getFeatureAvailability } from 'utils/featureToggle';
import { EVENT_TYPES } from 'utils/constants/appointmentsTypes';
import { isPendingToSyncEvent } from 'utils/validators/BulkCancel';
import { normalizeCrStatus, normalizeAthenaStatus } from 'utils/constants/planner';
import { selectedItemToSubstituteVar } from 'utils/cache/calendar';
import { isEditLockedEvent, EDIT_LOCKED_MESSAGE } from 'utils/validators/editLockedEvents';
import { toast } from 'react-toastify';

interface Props {
  setVisible: (val: boolean, shouldRemovePhantom?: boolean) => void;
  event: IEvent;
  handleCancel: () => void;
  configurations: IModalDownStreamsConfigurations;
  isObservation?: boolean;
  setIsAddObservationMode: Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
}
const HeaderView: React.FC<Props> = ({
  setVisible,
  event,
  handleCancel,
  configurations,
  isObservation,
  setIsAddObservationMode,
  isLoading
}: Props) => {
  const { isBulkCancelMode } = useContext(plannerContext);
  selectedItemToSubstituteVar(0);
  const isEditDisabled = React.useCallback(() => {
    return (
      configurations.editDisabled ||
      configurations.rescheduleDisabled ||
      isBulkCancelMode ||
      isEditLockedEvent(event?.id)
    );
  }, [configurations, isBulkCancelMode, event?.id]);

  const isCancelDisabled = React.useCallback(() => {
    return configurations.cancelDisabled || isBulkCancelMode;
  }, [configurations, isBulkCancelMode]);

  const editHandler = React.useCallback(() => {
    if (isEditLockedEvent(event?.id)) {
      toast.warn(EDIT_LOCKED_MESSAGE);
      return;
    }
    if (isEditDisabled()) return;
    formRedirectHandler({
      event,
      action: SIDEBAR_ACTIONS.EDIT
    });
  }, [isEditDisabled, event]);

  const cancelClickHandler = React.useCallback(() => {
    if (isCancelDisabled()) return;
    handleCancel();
  }, [handleCancel, isCancelDisabled]);
  const isFailedEvent = (event: IEvent) => {
    // Ignore CR status when CR display is disabled (treat as NO_SYNC), so a
    // CR-failed event no longer hides the Add Observation (eye) icon.
    const crStatus = normalizeCrStatus(event?.crStatus);
    // Ignore Athena status for DT when Athena display is disabled (normalized to
    // NO_SYNC), so a stale DT Athena failure no longer hides the Add Observation icon.
    const athenaStatus = normalizeAthenaStatus(
      event?.athenaStatus,
      event?.appointmentType?.eventType?.name
    );
    if (event?.appointmentType?.eventType?.name === EVENT_TYPES.DT)
      return crStatus! > 200 || athenaStatus! > 200;
    else return crStatus! > 200;
  };

  let isAddObservesFeatureEnabled =
    ((event?.appointmentType?.eventType?.name === EVENT_TYPES.ABA &&
      getFeatureAvailability(FEATURES.ABA_SHADOW_SCHEDULING)) ||
      (event?.appointmentType?.eventType?.name === EVENT_TYPES.DT &&
        getFeatureAvailability(FEATURES.SHADOW_SCHEDULING))) &&
    !isFailedEvent(event) &&
    event?.status !== EVENT_STATUS.canceled &&
    event?.provider?.isObservable &&
    !event?.isPendingConfirmation &&
    !isPendingToSyncEvent(normalizeCrStatus(event?.crStatus));
  return (
    <HeaderViewWrapper>
      <h2>Appointment</h2>
      <div className="icons-wrapper">
        {!isLoading && !!event && event?.status !== EVENT_STATUS.canceled && (
          <>
            {isAddObservesFeatureEnabled ? (
              <FontAwesomeIcon
                icon={faEye}
                onClick={() => {
                  setIsAddObservationMode(true);
                }}
              />
            ) : (
              <></>
            )}
            {!isObservation && (
              <FontAwesomeIcon
                style={{
                  cursor: isEditDisabled() ? 'not-allowed' : 'pointer'
                }}
                icon={faPen}
                onClick={editHandler}
              />
            )}
            <FontAwesomeIcon
              style={{
                cursor: isCancelDisabled() ? 'not-allowed' : 'pointer'
              }}
              icon={faBan}
              onClick={cancelClickHandler}
            />
          </>
        )}
        <CloseOutlined
          onClick={() => {
            setVisible(false, true);
          }}
        />
      </div>
    </HeaderViewWrapper>
  );
};
export default React.memo(HeaderView);
